'use client';
import { BookOpen, Sparkles } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { RefObject } from 'react';
import { Button } from '@/app/components/ui/button';
import { CLAW_LOGO_IMAGE_URL, CLAW_USAGE_GUIDE_URL } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import { useOpenPurchaseDialog } from '../../../_hook/use-open-purchase-dialog';
import type { ChatInputRef } from '../ChatInput';
import { WelcomeGameplayCases } from './components/WelcomeGameplayCases';
import { WelcomeQualityCases } from './components/WelcomeQualityCases';

interface WelcomeProps {
  chatInputRef?: RefObject<ChatInputRef | null>;
  /** 点 quality case 时回调：用于在打开 case 预览前清空当前输入草稿。 */
  onResetInput?: () => void;
}

export const Welcome = ({ chatInputRef, onResetInput }: WelcomeProps) => {
  const t = useTranslations();
  const openPurchaseDialog = useOpenPurchaseDialog();

  const clawPass = useStore((state) => state.clawPass);

  const userVariant = clawPass?.userVariant;

  return (
    <div className="mx-auto flex min-h-full w-full max-w-[900px] flex-col items-center justify-center gap-7 px-4 py-6 md:max-w-full lg:max-w-[720px]">
      {(userVariant === 'free' || userVariant === 'trial') && (
        <Button
          variant="default"
          className="sticky top-2 z-10 h-11 gap-2 rounded-full bg-[rgba(87,101,242,0.40)] light:bg-[#5765F2] px-6 font-medium light:text-white text-[#A7AAFF] text-sm hover:bg-[rgba(87,101,242,0.55)] light:hover:bg-[#4955D8]"
          onClick={() => {
            reportEvent('claw_element_click', { element_name: 'buy_video_plan' });
            openPurchaseDialog('feature');
          }}
        >
          <Sparkles className="size-5" />
          {t('claw.chat.welcome.buyVideoPlan')}
        </Button>
      )}

      <div className="mt-16 mb-10 flex shrink-0 flex-col items-center justify-center gap-2">
        <div className="flex items-center font-semibold text-[40px] text-system-white leading-[1.4]">
          <span>Vidu</span>
          <span className="mx-2 flex size-12 items-center justify-center overflow-hidden rounded-full bg-white">
            <img src={CLAW_LOGO_IMAGE_URL} alt={t('claw.nav.brand')} className="size-full object-cover" />
          </span>
          <span>Agent</span>
        </div>
        <div className="bg-[linear-gradient(90deg,#A7AAFF_0%,#CACBFF_100%)] light:bg-none bg-clip-text text-center light:text-[#5765F2] text-sm text-transparent leading-[14px]">
          {t('claw.chat.welcome.description')}
        </div>
        <a
          href={CLAW_USAGE_GUIDE_URL}
          target="_blank"
          rel="noreferrer noopener"
          onClick={() => reportEvent('claw_element_click', { element_name: 'usage_guide' })}
          className="group mt-3 inline-flex items-center gap-2 rounded-full border border-system-white12 light:border-[rgba(18,24,38,0.08)] bg-system-white04 light:bg-[rgba(18,24,38,0.03)] px-4 py-1.5 light:text-[#3D4A66] text-sm text-system-white64 transition-colors hover:border-system-white24 light:hover:border-[rgba(18,24,38,0.16)] hover:bg-system-white08 light:hover:bg-[rgba(18,24,38,0.06)] hover:text-system-white light:hover:text-[#1F2740]"
        >
          <BookOpen className="size-3.5" aria-hidden />
          <span>{t('claw.chat.welcome.usageGuide')}</span>
        </a>
      </div>

      <WelcomeQualityCases onContentClick={onResetInput} />

      <WelcomeGameplayCases onSelect={(item) => chatInputRef?.current?.applyQuickPrompt(item)} />
    </div>
  );
};
